import { useState, type FormEvent } from 'react';
import type { CandidatePhase, MatchInput } from '../lib/api.js';

interface Props {
  onSubmit: (input: MatchInput) => void;
}

const INTERESTS = [
  '起業',
  '社会課題',
  'デザイン思考',
  '英語不安',
  '自己理解',
  '進路',
  '教育',
  '海外体験',
  'スタートアップ',
  '研究',
];

const PHASES: { value: CandidatePhase; label: string; hint: string }[] = [
  { value: 'going', label: 'これから行く', hint: '出発前・準備中' },
  { value: 'deciding', label: '検討中', hint: '行くかどうか迷っている' },
  { value: 'returned', label: '帰国後', hint: 'すでに参加済み' },
  { value: 'family', label: '保護者・関係者', hint: '送り出す側の視点で' },
];

/**
 * Three-prompt onboarding (interests / phase / free text).
 * Interests is multi-select chips; phase is radio cards; free text is a
 * textarea. Free text drives the embedding-based portion of /match's
 * scoring, so we hint at that in the field copy.
 */
export function Onboarding({ onSubmit }: Props) {
  const [interests, setInterests] = useState<string[]>([]);
  const [phase, setPhase] = useState<CandidatePhase | null>(null);
  const [freeText, setFreeText] = useState('');

  const toggle = (tag: string) =>
    setInterests((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));

  const canSubmit = phase !== null && (interests.length > 0 || freeText.trim().length > 0);

  const handle = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !phase) return;
    const trimmed = freeText.trim();
    // Conditional spread keeps the API-input shape compatible with
    // exactOptionalPropertyTypes (free_text is absent rather than undefined).
    onSubmit({ interests, phase, ...(trimmed ? { free_text: trimmed } : {}) });
  };

  return (
    <div className="main">
      <div className="container">
        <h1>あなたに合う先輩を探します。</h1>
        <p className="lede">
          3つだけ教えてください。先輩がどんな話に応えやすいかを判断するのに使います。
        </p>

        <form onSubmit={handle}>
          <div className="field">
            <label className="field-label">関心のある領域(複数選択可)</label>
            <div className="chips" role="group" aria-label="関心のある領域">
              {INTERESTS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className="chip"
                  aria-pressed={interests.includes(tag)}
                  onClick={() => toggle(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field-label">いまの状況</label>
            <div className="phase-row" role="radiogroup" aria-label="状況">
              {PHASES.map((p) => (
                <label key={p.value} className={phase === p.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="phase"
                    value={p.value}
                    checked={phase === p.value}
                    onChange={() => setPhase(p.value)}
                  />
                  <div>
                    <div className="phase-pill">{p.label}</div>
                    <div className="phase-hint">{p.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="freetext">
              いま、何について話したいですか?(任意)
            </label>
            <textarea
              id="freetext"
              className="textarea"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder="例: 英語が不安だけど、人の役に立つ仕事に興味がある。技術より、誰かの困りごとを聞いて形にしたい。"
            />
            <p className="field-hint">
              自由記述があるほど、先輩の体験ログから関連する話を引き出しやすくなります。
            </p>
          </div>

          <div className="row">
            <div className="spacer" />
            <button type="submit" className="btn" disabled={!canSubmit}>
              先輩を探す
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
